<script lang="ts" setup>
  import mjimageForm from "./mjimageForm.vue";
  import mjgifForm from "./mjgifForm.vue";
  import mjdividerForm from "./mjdividerForm.vue";
  import mjbuttonForm from "./mjbuttonForm.vue";
  import mjspacerForm from "./mjspacerForm.vue";
  import mjtextForm from "./mjtextForm.vue";
  import mjrawForm from "./mjrawForm.vue";
  import mjvideoForm from "./mjvideoForm.vue";
  import mjsocialForm from "./mjsocialForm.vue";
  import mjdynamicForm from "./mjdynamicForm.vue";
  import lockedLayout from "./lockedLayout.vue";
  import lockingToggle from "./lockingToggle.vue";
  import aiContentInput from "./aiContentInput.vue";
  import mjcarouselForm from "./mjcarouselForm.vue";
  import customBlockForm from "./customBlockForm.vue";
  import apiblockform from "./APIBlockForm.vue";
  import mjloopBlockForm from "./mjloopBlockForm.vue";
  import mjratingForm from "./mjratingForm.vue";
  // import mjcoundownForm from "./mjcountdownForm.vue";
  import { v4 as uuidV4 } from "uuid";
  import { h as createElement } from "vue";
  import { useRolesStore } from "@/stores/roles";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useGeneralStore } from "@/stores/generalStore";
  import { TopolRawBlock } from "@/schemas/template/blocks/rawBlockSchema";
  import { TopolImageAndVideoBlock } from "@/schemas/template/blocks/imageAndVideoBlockSchema";
  import { computed } from "vue";

  const AI_LABELED_BLOCK_TYPES = ["mjtext", "mjimage", "mjbutton"];

  const rolesStore = useRolesStore();
  const mjmlStore = useMjmlStore();
  const generalStore = useGeneralStore();

  const type = computed(() => {
    if (mjmlStore.selectedBlock) {
      const sectionIndex = mjmlStore.selectedBlock.sectionIndex;

      const columnIndex = mjmlStore.selectedBlock.columnIndex;

      const blockIndex = mjmlStore.selectedBlock.blockIndex;
      if (checkStructure()) {
        if (
          mjmlStore.mjml.children[0].children[sectionIndex].children[
            columnIndex
          ].children[blockIndex].tagName
        ) {
          if (
            (
              mjmlStore.mjml.children[0].children[sectionIndex].children[
                columnIndex
              ].children[blockIndex] as TopolRawBlock
            ).custom
          ) {
            return "customBlock";
          }
          return mjmlStore.mjml.children[0].children[sectionIndex].children[
            columnIndex
          ].children[blockIndex].tagName.replace("-", "");
        } else {
          unselectBlock();
        }
      } else {
        unselectBlock();
      }
    }

    return "";
  });

  const isVideo = computed(() => {
    if (mjmlStore.selectedBlock) {
      const sectionIndex = mjmlStore.selectedBlock.sectionIndex;
      const columnIndex = mjmlStore.selectedBlock.columnIndex;
      const blockIndex = mjmlStore.selectedBlock.blockIndex;
      if (checkStructure()) {
        return (
          mjmlStore.mjml.children[0].children[sectionIndex].children[
            columnIndex
          ].children[blockIndex] as TopolImageAndVideoBlock
        ).isVideo;
      } else {
        unselectBlock();
        return false;
      }
    }

    return false;
  });

  const isLocked = computed(() => {
    return mjmlStore.getActiveBlock && mjmlStore.getActiveBlock.locked
      ? mjmlStore.getActiveBlock.locked
      : false;
  });

  const permissions = computed(() => {
    return rolesStore.permissions;
  });

  const showAiContent = computed(() => {
    if (!generalStore.aiLabeledElements) {
      return false;
    }
    if (type.value === "mjimage" && isVideo.value) {
      return false;
    }
    return AI_LABELED_BLOCK_TYPES.includes(type.value);
  });

  const key = computed(() => {
    return `block-form-${
      mjmlStore.getActiveBlock && mjmlStore.getActiveBlock.uid
        ? mjmlStore.getActiveBlock.uid
        : uuidV4()
    }${mjmlStore.langToShow ? `-${mjmlStore.langToShow}` : ""}`;
  });

  function unselectBlock() {
    mjmlStore.clearSelected();
  }

  function checkStructure() {
    //@ts-expect-error
    const sectionIndex = mjmlStore.selectedBlock.sectionIndex;
    //@ts-expect-error
    const columnIndex = mjmlStore.selectedBlock.columnIndex;
    //@ts-expect-error
    const blockIndex = mjmlStore.selectedBlock.blockIndex;
    if (
      mjmlStore.mjml.children &&
      mjmlStore.mjml.children[0] &&
      mjmlStore.mjml.children[0].children
    ) {
      if (
        mjmlStore.mjml.children[0].children[sectionIndex] &&
        mjmlStore.mjml.children[0].children[sectionIndex].children
      ) {
        if (
          mjmlStore.mjml.children[0].children[sectionIndex].children[
            columnIndex
          ] &&
          mjmlStore.mjml.children[0].children[sectionIndex].children[
            columnIndex
          ].children
        ) {
          if (
            mjmlStore.mjml.children[0].children[sectionIndex].children[
              columnIndex
            ].children[blockIndex]
          ) {
            return true;
          }
        }
      }
    }
    return false;
  }

  function getComponent() {
    const name = type.value;

    if (isVideo.value === true) {
      return mjvideoForm;
    }
    if (name === "mjimage") {
      return mjimageForm;
    }
    if (name === "mjgif") {
      return mjgifForm;
    }
    if (name === "mjbutton") {
      return mjbuttonForm;
    }
    if (name === "mjspacer") {
      return mjspacerForm;
    }
    if (name === "mjtext") {
      return mjtextForm;
    }
    if (name === "mjraw") {
      return mjrawForm;
    }
    if (name === "mjsocial") {
      return mjsocialForm;
    }
    if (name === "mjdivider") {
      return mjdividerForm;
    }
    if (name === "mjproduct") {
      return apiblockform;
    }
    if (name === "mjdynamic") {
      return mjdynamicForm;
    }
    if (name === "mjcarousel") {
      return mjcarouselForm;
    }
    if (name === "mjarticle") {
      return apiblockform;
    }
    if (name === "customBlock") {
      return customBlockForm;
    }
    if (name === "mjfeed-block") {
      return apiblockform;
    }
    if (name === "mjloop") {
      return mjloopBlockForm;
    }
    if (name === "mjrating") {
      return mjratingForm;
    }
  }

  const render = () => {
    const formComponent = getComponent();

    if (formComponent !== undefined) {
      if (permissions.value.canEditLockedBlocks || !isLocked.value) {
        const children = [
          createElement(formComponent, {
            isLocked: isLocked.value,
            permissions: permissions.value,
            key: key.value,
          }),
        ];

        children.push(
          createElement(lockingToggle, {
            isLocked: isLocked.value,
            canLockBlocks: permissions.value.canLockBlocks,
            key: `${key.value}-locking-toggle`,
          })
        );

        if (showAiContent.value) {
          children.push(
            createElement(aiContentInput, {
              target: "block",
              key: `${key.value}-ai-content`,
            })
          );
        }

        return createElement("div", children);
      } else {
        return createElement(lockedLayout, [
          createElement(formComponent, {
            isLocked: isLocked.value,
            permissions: permissions.value,
            key: key.value,
          }),
        ]);
      }
    }

    return createElement("div", []);
  };
</script>

<template>
  <render />
</template>
